import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, Tooltip, CircularProgress, Alert
} from '@mui/material';
import { Upload, Download, Delete, Description, Edit, Visibility } from '@mui/icons-material';
import { companyDocumentService } from '../../services/api';
import { usePermissions } from '../PermissionsContext';
import { toast } from 'react-toastify';
import { useConfirm } from '../Common/ConfirmDialog';

const CATEGORIES = ['Policy', 'Procedure', 'Template', 'Announcement', 'Other'];
const CATEGORY_COLORS = {
  Policy: 'primary', Procedure: 'secondary', Template: 'info',
  Announcement: 'warning', Other: 'default'
};

export default function CompanyDocumentsPage() {
  const confirm = useConfirm();
  const { hasPermission } = usePermissions();
  const canUpload = hasPermission('CompanyDocuments.Upload');
  const canEdit = hasPermission('CompanyDocuments.Edit');
  const canDelete = hasPermission('CompanyDocuments.Delete');

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editDoc, setEditDoc] = useState(null);

  // Upload form state
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Policy');
  const [description, setDescription] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');

  const fetchDocs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await companyDocumentService.getAll(filterCategory || undefined);
      setDocs(Array.isArray(res) ? res : (res?.data || []));
    } catch (err) {
      toast.error('Грешка при зареждане на документите');
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async () => {
    if (!file || !title.trim()) { toast.warning('Моля изберете файл и въведете заглавие'); return; }
    setUploading(true);
    try {
      await companyDocumentService.upload(file, title.trim(), category, description, effectiveDate || undefined);
      toast.success('Документът е качен успешно');
      setUploadOpen(false);
      setFile(null); setTitle(''); setDescription(''); setEffectiveDate('');
      fetchDocs();
    } catch (err) {
      toast.error('Грешка при качване');
    } finally {
      setUploading(false);
    }
  };

  const PREVIEWABLE_TYPES = [
    'application/pdf',
    'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml',
    'text/plain', 'text/html', 'text/csv',
    'audio/mpeg', 'audio/ogg', 'audio/wav',
    'video/mp4', 'video/webm'
  ];

  const canPreview = (contentType) => PREVIEWABLE_TYPES.some(t => contentType?.toLowerCase().startsWith(t));

  const handlePreview = async (doc) => {
    try {
      const res = await companyDocumentService.download(doc.id);
      window.open(res.sasUrl, '_blank');
    } catch (err) {
      toast.error('Грешка при преглед');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await companyDocumentService.download(doc.id);
      // Force download by fetching the blob and triggering save-as
      const response = await fetch(res.sasUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Грешка при изтегляне');
    }
  };

  const handleDelete = async (doc) => {
    if (!(await confirm({ message: `Сигурни ли сте, че искате да изтриете "${doc.title}"?`, danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await companyDocumentService.delete(doc.id);
      toast.success('Документът е изтрит');
      fetchDocs();
    } catch (err) {
      toast.error('Грешка при изтриване');
    }
  };

  const handleEditSave = async () => {
    if (!editDoc) return;
    try {
      await companyDocumentService.update(editDoc.id, {
        title: editDoc.title, category: editDoc.category, description: editDoc.description
      });
      toast.success('Документът е обновен');
      setEditDoc(null);
      fetchDocs();
    } catch (err) {
      toast.error('Грешка при обновяване');
    }
  };

  const formatSize = (bytes) => {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Фирмени документи</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Категория</InputLabel>
            <Select value={filterCategory} label="Категория" onChange={(e) => setFilterCategory(e.target.value)}>
              <MenuItem value="">Всички</MenuItem>
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          {canUpload && (
            <Button variant="contained" startIcon={<Upload />} onClick={() => setUploadOpen(true)}>
              Качи документ
            </Button>
          )}
        </Box>
      </Box>

      {loading ? <CircularProgress /> : docs.length === 0 ? (
        <Alert severity="info">Няма намерени документи</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Заглавие</TableCell>
                <TableCell>Категория</TableCell>
                <TableCell>Файл</TableCell>
                <TableCell>Размер</TableCell>
                <TableCell>Версия</TableCell>
                <TableCell>Качен от</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{doc.title}</Typography>
                        {doc.description && <Typography variant="caption" color="text.secondary">{doc.description}</Typography>}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={doc.category} size="small" color={CATEGORY_COLORS[doc.category] || 'default'} /></TableCell>
                  <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{doc.fileName}</Typography></TableCell>
                  <TableCell>{formatSize(doc.fileSize)}</TableCell>
                  <TableCell>v{doc.version}</TableCell>
                  <TableCell>{doc.uploadedBy || '—'}</TableCell>
                  <TableCell>{new Date(doc.uploadedAt).toLocaleDateString('bg-BG')}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Преглед"><IconButton size="small" onClick={() => canPreview(doc.contentType) ? handlePreview(doc) : handleDownload(doc)}><Visibility fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Изтегли"><IconButton size="small" onClick={() => handleDownload(doc)}><Download fontSize="small" /></IconButton></Tooltip>
                    {canEdit && <Tooltip title="Редактирай"><IconButton size="small" onClick={() => setEditDoc({...doc})}><Edit fontSize="small" /></IconButton></Tooltip>}
                    {canDelete && <Tooltip title="Изтрий"><IconButton size="small" color="error" onClick={() => handleDelete(doc)}><Delete fontSize="small" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Качване на фирмен документ</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Заглавие" value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth />
          <FormControl fullWidth>
            <InputLabel>Категория</InputLabel>
            <Select value={category} label="Категория" onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Описание" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={2} fullWidth />
          <TextField label="В сила от" type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <Button variant="outlined" component="label">
            {file ? file.name : 'Изберете файл'}
            <input type="file" hidden onChange={(e) => setFile(e.target.files[0])} />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Отказ</Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploading}>
            {uploading ? <CircularProgress size={20} /> : 'Качи'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDoc} onClose={() => setEditDoc(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Редактиране на документ</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {editDoc && <>
            <TextField label="Заглавие" value={editDoc.title} onChange={(e) => setEditDoc({...editDoc, title: e.target.value})} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Категория</InputLabel>
              <Select value={editDoc.category} label="Категория" onChange={(e) => setEditDoc({...editDoc, category: e.target.value})}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Описание" value={editDoc.description || ''} onChange={(e) => setEditDoc({...editDoc, description: e.target.value})} multiline rows={2} fullWidth />
          </>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDoc(null)}>Отказ</Button>
          <Button variant="contained" onClick={handleEditSave}>Запази</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
